
export default function UserEcosystem() {
  const users = [
    {
      role: 'Admin / Board Member',
      icon: 'ri-admin-line',
      color: '#1FA372',
      bgColor: '#F0FDF9',
      description: 'Oversees community governance, approves budgets, and makes strategic decisions for the association.',
      responsibilities: [
        'Approve maintenance requests',
        'Manage community budgets',
        'Enforce CC&Rs and bylaws',
        'Review violation reports'
      ],
      painPoints: [
        'Overwhelmed by paperwork',
        'Difficulty tracking compliance',
        'Poor visibility into finances'
      ]
    },
    {
      role: 'Property Manager',
      icon: 'ri-building-2-line',
      color: '#F97316',
      bgColor: '#FFF7ED',
      description: 'Handles day-to-day operations, vendor coordination, and serves as the primary point of contact.',
      responsibilities: [
        'Coordinate maintenance work',
        'Manage vendor relationships',
        'Handle resident inquiries',
        'Generate financial reports'
      ],
      painPoints: [
        'Juggling multiple communities',
        'Manual data entry',
        'Communication overload'
      ]
    },
    {
      role: 'Homeowner / Resident',
      icon: 'ri-home-heart-line',
      color: '#3B82F6',
      bgColor: '#EFF6FF',
      description: 'Lives in the community, pays dues, and expects seamless services and transparent communication.',
      responsibilities: [
        'Pay monthly dues on time',
        'Submit maintenance requests',
        'Participate in community votes',
        'Follow community guidelines'
      ],
      painPoints: [
        'Unclear payment status',
        'Slow request resolution',
        'Lack of transparency'
      ]
    },
    {
      role: 'Service Provider',
      icon: 'ri-tools-line',
      color: '#8B5CF6',
      bgColor: '#F5F3FF',
      description: 'External vendors providing maintenance, landscaping, security, and other community services.',
      responsibilities: [
        'Complete assigned work orders',
        'Submit invoices and reports',
        'Maintain service quality',
        'Communicate progress updates'
      ],
      painPoints: [
        'Unclear work scope',
        'Payment delays',
        'Poor communication channels'
      ]
    }
  ];

  return (
    <section className="py-24 bg-[#FAFBFC]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#F97316]/10 text-[#F97316] rounded-full text-sm font-semibold mb-4 border border-[#F97316]/20">
            03 — Users
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1E293B] mb-6">
            User Ecosystem & Role Mapping
          </h2>
          <p className="text-[#64748B] text-lg max-w-3xl mx-auto">
            Understanding the interconnected roles within HOA management to design experiences that serve each user's unique needs.
          </p>
        </div>

        {/* Ecosystem Diagram */}
        <div className="relative mb-16">
          <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-2 border-dashed border-[#E2E8F0] rounded-full"></div>
          <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border-2 border-dashed border-[#F1F5F9] rounded-full"></div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {users.map((user, index) => (
              <div 
                key={index}
                className="bg-white rounded-3xl p-6 border border-[#E2E8F0] hover:border-[#CBD5E1] hover:shadow-lg transition-all group relative overflow-hidden"
              >
                <div 
                  className="absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl opacity-30 group-hover:opacity-50 transition-opacity"
                  style={{ backgroundColor: user.color }}
                ></div>
                
                <div className="relative">
                  <div 
                    className="w-14 h-14 flex items-center justify-center rounded-2xl mb-4 border"
                    style={{ backgroundColor: user.bgColor, borderColor: `${user.color}30` }}
                  >
                    <i className={`${user.icon} text-2xl`} style={{ color: user.color }}></i>
                  </div>

                  <h3 className="text-xl font-bold text-[#1E293B] mb-2">{user.role}</h3>
                  <p className="text-[#64748B] text-sm mb-4 leading-relaxed">{user.description}</p>

                  <div className="mb-4">
                    <h4 className="text-[#1E293B] text-sm font-semibold mb-2 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line" style={{ color: user.color }}></i>
                      Key Responsibilities
                    </h4>
                    <ul className="space-y-1">
                      {user.responsibilities.map((item, idx) => (
                        <li key={idx} className="text-[#64748B] text-xs flex items-start gap-2">
                          <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: user.color }}></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-[#1E293B] text-sm font-semibold mb-2 flex items-center gap-2">
                      <i className="ri-error-warning-line text-[#EF4444]"></i>
                      Pain Points
                    </h4>
                    <ul className="space-y-1">
                      {user.painPoints.map((item, idx) => (
                        <li key={idx} className="text-[#EF4444] text-xs flex items-start gap-2">
                          <span className="w-1 h-1 bg-[#EF4444] rounded-full mt-1.5 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* User Journey Flow */}
        <div className="bg-white rounded-3xl p-8 border border-[#E2E8F0] shadow-sm">
          <h3 className="text-2xl font-bold text-[#1E293B] mb-8 text-center">User Interaction Flow</h3>
          <div className="flex flex-wrap justify-center items-center gap-4">
            <div className="flex items-center gap-3 bg-[#F0FDF9] px-4 py-3 rounded-xl border border-[#1FA372]/20">
              <i className="ri-admin-line text-[#1FA372] text-xl"></i>
              <span className="text-[#1E293B] text-sm font-medium">Admin</span>
            </div>
            <i className="ri-arrow-right-line text-[#CBD5E1] text-xl hidden md:block"></i>
            <i className="ri-arrow-down-line text-[#CBD5E1] text-xl md:hidden"></i>
            <div className="flex items-center gap-3 bg-[#FFF7ED] px-4 py-3 rounded-xl border border-[#F97316]/20">
              <i className="ri-building-2-line text-[#F97316] text-xl"></i>
              <span className="text-[#1E293B] text-sm font-medium">Property Manager</span>
            </div>
            <i className="ri-arrow-right-line text-[#CBD5E1] text-xl hidden md:block"></i>
            <i className="ri-arrow-down-line text-[#CBD5E1] text-xl md:hidden"></i>
            <div className="flex items-center gap-3 bg-[#EFF6FF] px-4 py-3 rounded-xl border border-[#3B82F6]/20">
              <i className="ri-home-heart-line text-[#3B82F6] text-xl"></i>
              <span className="text-[#1E293B] text-sm font-medium">Homeowner</span>
            </div>
            <i className="ri-arrow-right-line text-[#CBD5E1] text-xl hidden md:block"></i>
            <i className="ri-arrow-down-line text-[#CBD5E1] text-xl md:hidden"></i>
            <div className="flex items-center gap-3 bg-[#F5F3FF] px-4 py-3 rounded-xl border border-[#8B5CF6]/20">
              <i className="ri-tools-line text-[#8B5CF6] text-xl"></i>
              <span className="text-[#1E293B] text-sm font-medium">Service Provider</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
